import { useEffect, useState } from 'react';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import Head from 'next/head';
import { captureException } from '../lib/sentry';
import { useRouter } from 'next/router';

interface Window {
  Stripe: Stripe;
}

declare var window: Window;

export function useStripe({
  publishableKey,
  timeout
}: {
  publishableKey: string;
  timeout: number;
}): {
  loading: boolean;
  error: React.ReactElement | null;
  stripe?: Stripe;
  loadStripeJs: React.ReactElement;
} {
  const { query, pathname } = useRouter();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<React.ReactElement | null>(null);
  const [stripe, setStripe] = useState<Stripe | undefined>();
  // verify that Stripe have been loaded
  useEffect(() => {
    if (window.Stripe) {
      setLoading(false);
      return;
    }

    const intervalId = setInterval(() => {
      if (window.Stripe) {
        setLoading(false);
        clearInterval(intervalId);
      }
    }, 100);

    const timeoutId = setTimeout(() => {
      if (loading) {
        setError(
          <FormattedMessage
            id="stripe.script.error"
            defaultMessage="Could not load Stripe. Please disable any ad-blockers and try again."
          />
        );
        clearInterval(intervalId);
        const err = new Error('Could not load Stripe script');
        captureException(err, { query, pathname });
      }
    }, timeout);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [loading, timeout, query, pathname]);

  useEffect(() => {
    if (!loading && !stripe) {
      // @ts-ignore
      const _stripe = new window.Stripe(publishableKey, {
        apiVersion: '2022-11-15'
      });
      setStripe(_stripe);
    }
  }, [loading]);

  const loadStripeJs = (
    <Head>
      <script src="https://js.stripe.com/v3/" key="stripe-js"></script>
    </Head>
  );

  return { loading, error, stripe, loadStripeJs };
}
